import React, { createContext, useContext, useState, useEffect, useMemo } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const TOKEN_KEY = 'sed_auth_token';

// Hybrid auth: send the httpOnly cookie when the browser allows it, and ALSO
// send Authorization: Bearer <token> for environments where third-party cookies
// are blocked (Safari ITP, Chrome custom-domain → *.emergent.host, etc.).
axios.defaults.withCredentials = true;

const applyAuthHeader = (token) => {
  if (token) {
    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
  } else {
    delete axios.defaults.headers.common['Authorization'];
  }
};

// Restore on app boot before the first request fires
const bootToken = (() => {
  try { return localStorage.getItem(TOKEN_KEY); } catch { return null; }
})();
applyAuthHeader(bootToken);

const persistToken = (token) => {
  try {
    if (token) localStorage.setItem(TOKEN_KEY, token);
    else localStorage.removeItem(TOKEN_KEY);
  } catch { /* private mode / disabled storage */ }
  applyAuthHeader(token);
};

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchUser();
  }, []);

  const fetchUser = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/auth/me`);
      setUser(response.data);
    } catch {
      setUser(null);
      // If the stored token is no longer valid, drop it so we don't keep retrying.
      persistToken(null);
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    const response = await axios.post(`${API_URL}/api/auth/login`, { email, password });
    if (response.data?.token) persistToken(response.data.token);
    setUser(response.data.user);
    return response.data;
  };

  const register = async (name, email, password) => {
    const response = await axios.post(`${API_URL}/api/auth/register`, { name, email, password });
    if (response.data?.token) persistToken(response.data.token);
    setUser(response.data.user);
    return response.data;
  };

  const logout = async () => {
    // Optimistically clear UI state first
    setUser(null);
    persistToken(null);
    try {
      await axios.post(`${API_URL}/api/auth/logout`);
    } catch {
      // Ignore — token already cleared client-side
    }
  };

  const contextValue = useMemo(
    () => ({ user, loading, login, register, logout }),
    [user, loading]
  );

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};

// Exported for the accountant register/join flows that don't go through AuthContext
export { persistToken };
